import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { AIContentService, AIContentRequest, AIContentResponse } from 'src/app/services/ai-content.service';
import { AiChatSelectionService, AISelectionData } from 'src/app/services/ai-selection.service';
import { takeUntil, Subject } from 'rxjs';
import { CardsService } from 'src/app/services/cards.service';
import { BreadcrumbComponent } from 'src/app/components/shared/breadcrumb/breadcrumb.component';

@Component({
  selector: 'app-ai-chat',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    BreadcrumbComponent,
  ],
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss'],
})
export class AiChatComponent implements OnInit {
  chatForm: FormGroup;
  loading = false;
  error = '';
  suggestions: any[] = [];
  hasResponse = false;
  selectedIndex: number | null = null;

  // Info about origin page
  originCardId: string | null = null;
  originType: string | null = null;
  returnUrl: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private aiContentService: AIContentService,
    private selectionService: AiChatSelectionService,
    private router: Router,
    private route: ActivatedRoute,
    private cardsService: CardsService
  ) {
    this.chatForm = this.fb.group({ userMessage: [''] });
  }

  ngOnInit(): void {
    // Clear any stale sessionStorage data when entering AI chat
    sessionStorage.removeItem('aiSelection');
    console.log('💾 Option 2: Cleared any existing AI selection data');

    // Get origin info from query params
    this.originCardId = this.route.snapshot.queryParamMap.get('cardId');
    this.originType = this.route.snapshot.queryParamMap.get('type');
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || null;
    const initialMessage = this.route.snapshot.queryParamMap.get('initialMessage');

    // Auto-populate and send initial AI message if available
    if (initialMessage) {
      this.chatForm.patchValue({ userMessage: initialMessage });
      this.sendMessage();
    }
  }

  sendMessage(): void {
    const message = this.chatForm.get('userMessage')?.value?.trim();
    if (!message) return;

    // Get the selected card to get the cardid
    const selectedCard = this.cardsService.selectedCard();
    if (!selectedCard) {
      this.error = 'No card selected';
      return;
    }

    this.loading = true;
    this.error = '';
    this.hasResponse = false;
    this.selectedIndex = null;
    this.suggestions = [];

    // Create the request for AI content generation
    const request: AIContentRequest = {
      cardid: selectedCard.id,
      type: this.originType === 'service' ? 'service' : 'product',
      description: message, // Send user's message as description
      language: 'English'
    };

    this.aiContentService.generateContent(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: AIContentResponse) => {
          this.loading = false;

          // Map the suggestions from the AI response
          this.suggestions = res.suggestions?.map((item) => ({
            image: item.image || '', // Use image from response, empty string as fallback
            name: item.name || 'Untitled',
            description: item.description || '',
            features: item.features || [], // Use features array from response
            price: null,
            currency: 'INR',
          })) || [];

          this.hasResponse = true;
        },
        error: (err: any) => {
          this.loading = false;
          this.error = err?.message || 'AI request failed';
        },
      });
  }

  selectSuggestion(index: number): void {
    this.selectedIndex = index;
  }

  applySelected(): void {
    if (this.selectedIndex === null) return;

    const suggestion = this.suggestions[this.selectedIndex];

    // Option 3: Enhanced Service with Persistence
    const selectionData: AISelectionData = {
      name: suggestion.name ?? '',
      description: suggestion.description ?? '',
      images: suggestion.image ? [suggestion.image] : [],
      features: suggestion.features ?? [],
      type: (this.originType as 'product' | 'service') ?? 'product',
      price: suggestion.price ?? 0,
      currency: suggestion.currency ?? 'INR'
    };

    console.log('� Option 3: Applying AI selection via enhanced service:', selectionData);

    // Use enhanced service to emit selection with persistence
    this.selectionService.emitSelection(selectionData);

    // Small delay to ensure service processes the data
    setTimeout(() => {
      // Navigate back
      if (this.returnUrl) {
        console.log('💾 Option 3: Navigating back to returnUrl:', this.returnUrl);
        // Use navigateByUrl to handle full URLs with query parameters
        this.router.navigateByUrl(this.returnUrl).then(
          success => console.log('✅ Navigation successful:', success),
          error => console.error('❌ Navigation failed:', error)
        );
      } else {
        console.log('💾 Option 3: Navigating to default listing page');
        this.router.navigate(['/listing']).then(
          success => console.log('✅ Default navigation successful:', success),
          error => console.error('❌ Default navigation failed:', error)
        );
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
